/**
 * Cliente HTTP para a API EvoluPsi.
 * Base URL via VITE_API_BASE_URL; token em localStorage (evolupsi-token).
 * Em 401 remove o token e dispara evento para o app mostrar login.
 *
 * `createApiClient` permite que outro front (o backoffice, em apps/backoffice)
 * use o mesmo cliente com a sua própria chave de token e evento de logout.
 */

export type ApiError = { status: number; message: string; data?: unknown };

type RequestOptions = RequestInit & { skipAuth?: boolean };

function getBaseUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL;
  if (url) return url.replace(/\/$/, '');
  // Produção atrás do Nginx do container: mesma origem que o SPA (/auth → API)
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:4000';
}

export function createApiClient({ tokenKey, unauthorizedEvent }: { tokenKey: string; unauthorizedEvent: string }) {
  const getToken = (): string | null => localStorage.getItem(tokenKey);
  const setToken = (token: string): void => localStorage.setItem(tokenKey, token);
  const clearToken = (): void => localStorage.removeItem(tokenKey);

  /** Dispara logout no app (quem usa o cliente escuta `unauthorizedEvent`) */
  const onUnauthorized = (): void => {
    clearToken();
    window.dispatchEvent(new CustomEvent(unauthorizedEvent));
  };

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;
    const url = `${getBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...((fetchOptions.headers as Record<string, string>) ?? {}),
    };
    if (!skipAuth) {
      const token = getToken();
      if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    const res = await fetch(url, { ...fetchOptions, headers });
    if (res.status === 401) {
      onUnauthorized();
      const text = await res.text();
      let message = 'Sessão expirada. Faça login novamente.';
      try {
        const j = JSON.parse(text);
        if (j.message) message = j.message;
      } catch {
        // ignore
      }
      throw { status: 401, message, data: text };
    }
    if (!res.ok) {
      const text = await res.text();
      let message = `Erro ${res.status}`;
      try {
        const j = JSON.parse(text);
        message = j.message ?? j.error ?? message;
      } catch {
        if (text) message = text.slice(0, 200);
      }
      throw { status: res.status, message, data: text } as ApiError;
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }

  const api = {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'GET' }),

    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'POST', body: body ? JSON.stringify(body) : undefined }),

    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),

    delete: <T>(path: string, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'DELETE' }),
  };

  return {
    api,
    getToken,
    setToken,
    clearToken,
    hasToken: (): boolean => !!getToken(),
    onUnauthorized,
  };
}

const UNAUTHORIZED_EVENT = 'evolupsi-unauthorized';
const client = createApiClient({ tokenKey: 'evolupsi-token', unauthorizedEvent: UNAUTHORIZED_EVENT });

export const api = client.api;
export const setToken = client.setToken;
export const clearToken = client.clearToken;
export const hasToken = client.hasToken;
export const onUnauthorized = client.onUnauthorized;
export const getAuthToken = client.getToken;
export const UNAUTHORIZED_EVENT_NAME = UNAUTHORIZED_EVENT;

export function getApiBaseUrl(): string {
  return getBaseUrl();
}
